// Fix II (2026-10-30): «Por almacén» pasa a «Por obra»: primero el Almacén Central y después cada obra con su Almacén
// de obra y su Depósito en obra (Stock CEN, Proveedor externo y Caja Chica).
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ChevronDown, ChevronRight, Building2, Warehouse, HardHat } from "lucide-react";
import { API } from "../../contexts/AuthContext";
import { coincide } from "../../components/SelectBuscable";
import DepositoObraPanel from "./DepositoObraPanel";

const opts = { withCredentials: true };

function AlmacenObraBloque({ almacen, lineas }) {
  const [reservas, setReservas] = useState(null);
  useEffect(() => {
    axios.get(`${API}/inventory/almacenes-obra/${encodeURIComponent(almacen.id)}/reservas`, opts)
      .then(({ data }) => setReservas(data.reservas || [])).catch(() => setReservas([]));
  }, [almacen.id]);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`obra-almacen-${almacen.id}`}>
      <p className="px-4 py-2 text-sm font-bold text-[#1A4B7A] bg-gray-50 border-b border-gray-100 flex items-center gap-1.5"><Warehouse size={14} /> {almacen.nombre} <span className="text-xs font-normal text-gray-500">· en el Depósito Central</span></p>
      {lineas.length === 0 && (reservas || []).length === 0 ? (
        <p className="px-4 py-3 text-xs text-gray-400 italic">Sin materiales en este almacén.</p>
      ) : (
        <div className="divide-y divide-gray-50">
          {lineas.map(l => (
            <div key={l.m.id} className="px-4 py-2 flex items-center justify-between gap-3 text-sm">
              <span className="text-gray-800"><span className="text-xs font-mono text-gray-400 mr-1.5">{l.m.code}</span>{l.m.name}</span>
              <span className="font-semibold text-gray-900 tabular-nums whitespace-nowrap">{l.cantidad} {l.m.unit}</span>
            </div>
          ))}
          {(reservas || []).map((r, i) => (
            <div key={`r${i}`} className="px-4 py-2 flex items-center justify-between gap-3 text-sm bg-amber-50/40" data-testid={`obra-reserva-${almacen.id}-${i}`}>
              <span className="text-gray-800">{r.nombre} <span className="text-xs text-amber-700">· reservado para retiro ({r.retiro}{r.rsi ? ` · ${r.rsi}` : ""})</span></span>
              <span className="font-semibold text-amber-700 tabular-nums whitespace-nowrap">{r.cantidad} {r.unidad}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PorObraVista({ filtro, porAlmacen, senal, onObras }) {
  const [obras, setObras] = useState(null);
  const [abiertas, setAbiertas] = useState({});
  useEffect(() => {
    axios.get(`${API}/inventory/obras-inventario`, opts).then(({ data }) => { setObras(data || []); onObras?.(data || []); })   // Fix IM
      .catch(e => { setObras([]); toast.error(e?.response?.data?.detail || "No se pudieron cargar las obras"); });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const lista = (obras || []).filter(o => !(filtro || "").trim() || coincide(`${o.obra_nombre} ${o.obra_codigo || ""} ${o.almacenes_obra.map(a => a.nombre).join(" ")} ${o.depositos.map(d => d.nombre).join(" ")}`, filtro));
  const clave = (o) => o.obra_id || o.project_id || o.obra_nombre;
  useEffect(() => {
    if (senal) setAbiertas(Object.fromEntries((obras || []).map(o => [clave(o), senal.abrir])));
  }, [senal]);   // eslint-disable-line react-hooks/exhaustive-deps
  const lineasDe = (id) => (porAlmacen.find(a => a.id === id) || { lineas: [] }).lineas;
  if (obras === null) return <div className="flex items-center gap-2 text-sm text-gray-500 p-4"><Loader2 size={15} className="animate-spin" /> Cargando obras...</div>;
  if (lista.length === 0) return <p className="text-sm text-gray-400 italic bg-white rounded-xl border border-gray-200/60 p-6 text-center" data-testid="por-obra-vacio">{(filtro || "").trim() ? "Ninguna obra coincide con el filtro" : "Todavía no hay obras habilitadas en Apertura/Cierre"}</p>;
  return (
    <div className="space-y-3" data-testid="por-obra">
      {lista.map(o => {
        const k = clave(o);
        const abierta = !!abiertas[k];
        const items = o.depositos.reduce((s, d) => s + (d.items_con_saldo || 0), 0);
        return (
          <div key={k} className="bg-white rounded-xl border border-gray-200/60 overflow-hidden" data-testid={`obra-card-${k}`}>
            <button type="button" onClick={() => setAbiertas(s => ({ ...s, [k]: !s[k] }))}
              className="w-full px-4 py-3 bg-[#EBF3FB] hover:bg-[#DDEBF8] flex items-center justify-between gap-3 text-left" data-testid={`obra-toggle-${k}`}>
              <div className="flex items-center gap-2 min-w-0">
                {abierta ? <ChevronDown size={15} className="text-[#1A4B7A] flex-shrink-0" /> : <ChevronRight size={15} className="text-[#1A4B7A] flex-shrink-0" />}
                <HardHat size={15} className="text-[#1A4B7A] flex-shrink-0" />
                <span className="text-sm font-bold text-[#1A4B7A] truncate">{o.obra_nombre}{o.obra_codigo ? ` (${o.obra_codigo})` : ""}</span>
                {o.obra_rapida && <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 flex-shrink-0" data-testid={`obra-rapida-${k}`}>Obra rápida</span>}{/* Fix IL */}
              </div>
              <span className="text-xs font-semibold text-[#1A4B7A] whitespace-nowrap">{items} ítem{items !== 1 ? "s" : ""} en depósito</span>
            </button>
            {abierta && (
              <div className="p-3 space-y-3">
                {o.almacenes_obra.map(a => <AlmacenObraBloque key={a.id} almacen={a} lineas={lineasDe(a.id)} />)}
                {o.depositos.map(d => (
                  <div key={d.id} className="space-y-2" data-testid={`obra-deposito-${d.id}`}>
                    <p className="text-sm font-bold text-[#1A4B7A] flex items-center gap-1.5"><Building2 size={14} /> {d.nombre} <span className="text-xs font-normal text-gray-500">· en la obra</span></p>
                    <DepositoObraPanel depositoId={d.id} />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
